<template>
  <!-- PROJECT INFO -->
  <div class="mx-auto w-75 pa-4">
    <v-card class="d-flex justify-space-between">
      <div class="d-flex">
        <!-- AVATAR  -->
        <v-avatar size="200" class="pl-8">
          <v-img :src="getLogo()" :cover="false" />
        </v-avatar>
        <div class="d-flex flex-column pl-8 pt-4">
          <!-- CLIENT NAME -->
          <div class="font-weight-bold text-h3 pt-8">{{ (props.id).replace("dep-", " ") }}</div>
          <!-- SUBTITLE -->
          <div v-if="project?.firebaseConfig" class="text-h6 text-grey-darken-2 pt-4 italic"> {{
            project.firebaseConfig.projectId }}</div>
        </div>
      </div>
      <!-- BUTTONS -->
      <div class="d-flex flex-column pt-8 mr-12 gap-4">
        <v-btn size="large" color="indigo-darken-4" @click="toggleEditing()">{{ editButtonText }}</v-btn>
        <v-btn size="large" color="indigo-darken-4" @click="isAddingSection = true">ADD SECTION</v-btn>

        <!-- Add section dialog -->
        <v-dialog v-model="isAddingSection" max-width="600px">
          <v-card>
            <v-card-title>
              Add section
            </v-card-title>
            <v-card-text>
              <v-form @submit.prevent="submitAddSection">
                <!-- Section name -->
                <v-text-field v-model="newSectionModel.title" label="Section title" required></v-text-field>
                <!-- New field name -->
                <v-text-field v-model="newSectionModel.name" label="Field title" required></v-text-field>
                <!-- New field type-->
                <v-select v-model="newSectionModel.type" :items="['Text', 'Number', 'Boolean', 'Date']"
                  label="Field type"></v-select>
                <!-- New field value -->
                <div v-if="newSectionModel.type === 'Text'">
                  <v-text-field v-model="newSectionModel.text" label="Field value" required></v-text-field>
                </div>
                <div v-if="newSectionModel.type === 'Number'">
                  <v-number-input v-model="newSectionModel.text" label="Field value" required></v-number-input>
                </div>
                <div v-if="newSectionModel.type === 'Boolean'">
                  <v-switch v-model="newSectionModel.text" label="Field value" color="indigo darken-4"></v-switch>
                </div>
                <div v-if="newSectionModel.type === 'Date'">
                  <v-date-input v-model="newSectionModel.text" label="Field value" prepend-icon=""></v-date-input>
                </div>
                <v-switch v-model="newSectionModel.editable" label="Editable" color="indigo darken-4"></v-switch>

              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <!-- Cancel button -->
              <v-btn color="indigo darken-4" text @click="isAddingSection = false">Cancel</v-btn>
              <!-- Add button -->
              <v-btn color="indigo darken-4" text @click="submitAddSection">Add section</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </div>
    </v-card>

    <!-- Tabs name-->
    <v-tabs v-model="activeTab" color="indigo-darken-4" class="mt-8">
      <v-tab value="client">Client Info</v-tab>
      <v-tab value="billing">Billing</v-tab>
    </v-tabs>

    <!-- Tabs content-->
    <v-card-text>
      <v-tabs-window v-model="activeTab">
        <v-tabs-window-item value="client">
          <!-- Cards container -->
          <div v-if="project" class="w-full flex flex-wrap justify-between fields-container">
            <!-- Loop to display all fields -->
            <div v-for="(data, index) in cardsData" :key="index" class="w-[45%] flex mr-1 ml-1 mb-1">
              <client-card :project="project" :data="data" :isEditing="isEditing" :editableFields="editableFields">
              </client-card>
            </div>
          </div>
        </v-tabs-window-item>

        <v-tabs-window-item value="billing">
          Two
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card-text>

  </div>

</template>

<style scoped>
.v-tab .v-icon {
  color: red;
  /* Cambia esto al color que desees */
}
</style>

<script setup>
import { defineProps, watch } from 'vue';
import { ref, computed } from 'vue';
import * as rosepetalModel from "rosepetal-model";
import { onMounted } from 'vue';
import ClientCard from '@/components/client/Main.vue';

const props = defineProps({
  id: String,
});
const project = ref();
const cardsData = ref([]);
const activeTab = ref();
const isEditing = ref(history.state?.isPageEditable ?? false);
let $meta = rosepetalModel.default.meta;
const editButtonText = computed(() => (isEditing.value ? 'SAVE' : 'EDIT'));
const editableFields = ref({});
const isAddingSection = ref(false);
const newSectionModel = ref({
  name: null,
  title: null,
  text: null,
  editable: false,
  type: null
});

function resetNewSectionModel() {
  newSectionModel.value.title = '';
  newSectionModel.value.name = '';
  newSectionModel.value.text = null;
  newSectionModel.value.type = null;
  newSectionModel.value.editable = false;
}

function resetValue() {
  newSectionModel.value.text = null;
}

function toggleEditing() {
  isEditing.value = !isEditing.value;
}

// Gets the editable fields from the firestore and updates the editableFields object
async function getEditableFields() {
  editableFields.value = await $meta.getEditableFields();

  if (project.value?.metadata?.editableFields) {
    editableFields.value = {
      ...editableFields.value,
      ...project.value.metadata.editableFields
    }
  }
}

// Gets the client logo and returns it as a png
function getLogo() {
  if (project.value?.metadata?.logo) return `data:image/png;base64, ${project.value.metadata.logo}`;
  return require(`@/assets/logos/rp_isotype_white.png`);
}

// Gets the project data from the firestore and updates the project object
async function getProjectData(id) {
  const projectDoc = await $meta.getProjectData(id);
  project.value = projectDoc.project.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }))[0];
  project.value.metadata = projectDoc.metadata;
  project.value.metadata.active = Boolean(project.value.metadata.active)
  // Get the zerotier info
  getOnline();

  // Organize the data for client view card generation
  setCardData();
}

// Submit a new field to the firestore
function submitAddSection() {
  isAddingSection.value = false;
  const newSection = {
    location: newSectionModel.value.title,
    name: newSectionModel.value.name,
    text: newSectionModel.value.text,
    editable: newSectionModel.value.editable,
    type: newSectionModel.value.type,
  };

  $meta.setNewFields(newSection, props.id);
  // Add the new section to the card data
  if (newSectionModel.value.type === 'Date') {
    newSectionModel.value.text = new Date(newSectionModel.value.text).toLocaleString();
  }
  cardsData.value.push({
    title: newSectionModel.value.title,
    fields: [
      {
        name: newSectionModel.value.name,
        text: newSectionModel.value.text,
        editable: newSectionModel.value.editable
      },
    ],
  })

  // Add the new field to the editableFields object
  editableFields.value[newSectionModel.value.name] = newSectionModel.value.editable;
  // Reset the new section model
  resetNewSectionModel();
}


// Gets the zerotier info and updates the project object
async function getOnline() {
  const peers = await $meta.getPeers();
  const matchingServers = peers.filter((server) => server.name.includes(project.value.id));
  const projectDetails = matchingServers.map((server) => ({
    name: server.name,
    ip: server.ip,
    online: server.online,
  }));
  project.value.servers = projectDetails[0]
}

// Organize the data for client view card generation
const setCardData = () => {

  // Add a field to this array if you don't want to show it in any section of the client view
  const excludedFields = ["logo", "editableFields", "metadataId"]
  // Add a section (map field on the firestore) to this array if you want its fields to show on the "Profile" section
  const profileFields = ["functions", "nodeConfig"]

  // Add the "Profile" section 
  cardsData.value.push({
    title: "Profile",
    fields: [],
  });

  Object.keys(project.value).forEach((key) => {

    // This loop adds the map fields that are included in the profileFields array to the "Profile" section (cardsData.value[0])
    if (profileFields.includes(key) && typeof project.value[key] !== "string") {
      Object.keys(project.value[key]).forEach((subKey) => {
        // If the field is not included do not add it
        // if (excludedFields.includes(subKey)) return;
        // cardsData.value[0].fields.push({
        //   name: subKey,
        //   text: project.value[key][subKey],
        // });
      });
      return
    }

    // This loop adds the fields that are not map fields to the "Profile" section
    // If the field is not included do not add it
    if (excludedFields.includes(key)) return;
    cardsData.value[0].fields.push({
      name: key,
      text: project.value[key],
    });

    // This loop creates a new section for every firestore map field that is not included in the profileFields array
    // if the value is an object and is not a date, add a new section
    if (typeof project.value[key] == "object" && !Object.hasOwn(project.value[key], "seconds")) {
      cardsData.value.push({
        title: key,
        fields: [],
      });

      Object.keys(project.value[key]).forEach((subKey) => {
        // If the field is not included do not add it
        if (excludedFields.includes(subKey)) return;
        cardsData.value[cardsData.value.length - 1].fields.push({
          name: subKey,
          text: project.value[key][subKey],
        });
      });
    }

  });
  cardsData.value.sort((a, b) => (a.title === "Profile" ? -1 : b.title === "Profile" ? 1 : 0));
  // Sort the sections alphabetically
  cardsData.value.forEach((card) => {
    card.fields.sort((a, b) => a.name.localeCompare(b.name));
  });

}

onMounted(async () => {
  await getProjectData(props.id);
  await getEditableFields();
});

// Reset the new section model when the add section dialog is closed
watch(() => isAddingSection.value, (newValue) => {
  if (!newValue) {
    resetNewSectionModel();
  }
});

// Reset the value when the user changes the field type

watch(() => newSectionModel.value.type, (newValue) => {
  if (newValue === 'Boolean') {
    newSectionModel.value.text = false;
    return;
  }
  if (newValue) {
    resetValue();
  }
});

</script>
